import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Play, RefreshCw, Loader2, Clock, Brain, Mail, AlertTriangle, Target, Star } from "lucide-react";
import { toast } from "sonner";

const JOB_META = {
  briefing_diretor: { label: "Briefing executivo", schedule: "Diário · 08:00", icon: Brain, color: "#1B3A6B" },
  alerta_vencimento: { label: "Alerta de vencimento", schedule: "Diário · 09:00", icon: AlertTriangle, color: "#eab308" },
  inadimplencia: { label: "Inadimplência", schedule: "Diário · 10:00", icon: AlertTriangle, color: "#dc2626" },
  justificativas_pendentes: { label: "Tickets sem update >24h", schedule: "A cada 2h", icon: Clock, color: "#F97316" },
  meta_baixa: { label: "Vendedor abaixo da meta", schedule: "Dia 15 · 08:00", icon: Target, color: "#7c3aed" },
  nps_semanal: { label: "NPS semanal", schedule: "Segundas · 08:00", icon: Star, color: "#0891b2" },
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [running, setRunning] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/jobs/disponiveis"),
      api.get("/jobs/historico"),
      api.get("/briefings"),
    ]);
    setJobs(a.data);
    setHistorico(b.data);
    setBriefings(c.data);
  };
  useEffect(() => { load(); }, []);

  const run = async (name) => {
    setRunning(name);
    try {
      const { data } = await api.post(`/jobs/executar/${name}`);
      toast.success(`Job ${name} concluído`);
      load();
    } catch (e) {
      toast.error(`Falha em ${name}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(null);
    }
  };

  const lastRunFor = (name) => historico.find((h) => h.name === name);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
          Cron Jobs & Automação
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Execute manualmente ou monitore os jobs agendados. Todos rodam automaticamente nos horários indicados.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobs.map((j) => {
          const meta = JOB_META[j.name] || { label: j.name, schedule: "—", icon: Clock, color: "#6b7280" };
          const Icon = meta.icon;
          const last = lastRunFor(j.name);
          return (
            <Card key={j.name} className="p-5 border-l-4" style={{ borderLeftColor: meta.color }} data-testid={`job-${j.name}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${meta.color}1f`, color: meta.color }}>
                    <Icon size={16} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-sm" style={{ color: "#1B3A6B" }}>{meta.label}</h3>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{meta.schedule}</div>
                  </div>
                </div>
              </div>
              {last ? (
                <div className="text-xs text-muted-foreground mb-3">
                  Última execução: <strong>{new Date(last.ran_at).toLocaleString("pt-BR")}</strong>
                  <pre className="mt-1 text-[10px] bg-slate-50 p-2 rounded">{JSON.stringify(last.summary, null, 2).slice(0, 200)}</pre>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mb-3 italic">Ainda não executado.</div>
              )}
              <Button
                size="sm"
                onClick={() => run(j.name)}
                disabled={running === j.name}
                className="w-full text-white"
                style={{ background: "#F97316" }}
                data-testid={`run-${j.name}`}
              >
                {running === j.name ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Play size={12} className="mr-2" />}
                Executar agora
              </Button>
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold" style={{ color: "#1B3A6B" }}>Briefings históricos</h3>
          <Button variant="outline" size="sm" onClick={load} data-testid="jobs-refresh"><RefreshCw size={12} className="mr-2" /> Atualizar</Button>
        </div>
        {briefings.length === 0 && <p className="text-sm text-muted-foreground">Nenhum briefing gerado ainda.</p>}
        <div className="space-y-2">
          {briefings.map((b) => (
            <div key={b.id} className="flex items-center justify-between p-3 border rounded-md hover:border-orange-400 transition" data-testid={`brief-${b.id}`}>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#1B3A6B" }}>{new Date(b.data).toLocaleString("pt-BR")}</div>
                <div className="text-xs text-muted-foreground line-clamp-1 max-w-2xl">{b.texto_ia.slice(0, 140)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setDetail(b)}>Ler completo</Button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Briefing · {detail && new Date(detail.data).toLocaleString("pt-BR")}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div>
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{detail.texto_ia}</pre>
              <details className="mt-4 text-xs text-muted-foreground">
                <summary className="cursor-pointer">Contexto utilizado</summary>
                <pre className="mt-2 bg-slate-50 p-3 rounded">{JSON.stringify(detail.contexto, null, 2)}</pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
